// Full-document render of the sealy ProductLineCollectionPage: PageSkeleton
// (the React document shell) wrapping the legacy
// product_line_collection_page.html content block -- the PLC hero (server
// hero <picture> + hero-gallery island + the PLC-configurator mount div), the
// sticky configurator, the sticky-trigger anchor, and the consolidated body
// -- with the container DOM the SCSS/JS anchors to (#product-detail-container
// [data-cms], #product-detail, .pdp-hero.plc-hero, .pdp-sticky-configurator,
// .plc-sticky-trigger).
import React from "react";

import { templates } from "@reactivated";

import { PLCConfiguratorApp } from "../apps/product-detail/PLCConfiguratorApp";
import { PageSkeleton } from "../components/PageSkeleton";
import { ProductLineCollectionBody } from "../components/ProductLineCollectionBody";
import { ProductLineCollectionHeroGallery } from "../components/ProductLineCollectionHeroGallery";
import { Template as StickyConfigurator } from "./StickyConfiguratorTemplate";

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the
// in-tree SiteHeader chain, which is typed with mutable reactivated props.
// The hero-gallery/body templates accept Immutable props, to which these are
// assignable.
export const Template = (
    props: templates.ProductLineCollectionPageTemplate,
): React.JSX.Element => {
    const c = props.plc_configurator;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {/* data-cms stays: the PLC configurator's CMS payload (upsell modal,
                delivery copy, shipping lead/icon) is threaded to
                PLCConfiguratorApp as the cms_json prop below, and the same value
                is retained on this attribute for the server-render contract
                (test_plc_page.py asserts data-cms survives + parses as JSON). */}
            <div id="product-detail-container" data-cms={props.cms_json}>
                <div id="product-detail">
                    <div className="pdp-hero plc-hero">
                        {props.hero_image && (
                            <picture className="pdp-hero__image">
                                {props.hero_image.mobile_image_url && (
                                    <source
                                        srcSet={
                                            props.hero_image.mobile_image_url
                                        }
                                        media="(max-width: 768px)"
                                    />
                                )}
                                <img
                                    src={props.hero_image.image_url}
                                    alt={props.hero_image.image_alt}
                                    fetchPriority="high"
                                />
                            </picture>
                        )}
                        <ProductLineCollectionHeroGallery
                            page={props.page}
                            gallery_ssr={props.gallery_ssr}
                        />
                        {/* The PLC-configurator mount div (the legacy
                            product-line-collection-configurator.html partial).
                            The configurator now renders inside the reactivated
                            document tree (client-only, via PLCConfiguratorApp),
                            fed by props.plc_configurator + props.cms_json rather
                            than the data-* attributes the old imperative island
                            read off the DOM. The data-place-react attribute
                            stays: the PLC SCSS anchors to it
                            ([data-place-react="product-line-collection-configurator"])
                            and test_plc_page.py asserts its presence. */}
                        <div data-place-react="product-line-collection-configurator">
                            <PLCConfiguratorApp
                                configurator={c}
                                cmsJson={props.cms_json}
                            />
                        </div>
                    </div>
                    {/* The sticky-configurator island in its legacy
                        sticky-configurator.html wrapper div. */}
                    <div className="pdp-sticky-configurator">
                        <StickyConfigurator {...props.sticky_configurator} />
                    </div>
                    {/* Byte-exact: the StickyConfigurator island observes this
                        selector (sticky_trigger_selector) to toggle the
                        sticky rail. */}
                    <span className="plc-sticky-trigger"></span>
                    <ProductLineCollectionBody
                        page={props.page}
                        reviews={props.reviews}
                        page_num_reviews={props.page_num_reviews}
                        change_variant_ssr={props.change_variant_ssr}
                        change_root_ssr={props.change_root_ssr}
                        root_conditional_ssr={props.root_conditional_ssr}
                        variant_conditional_ssr={props.variant_conditional_ssr}
                        tab_cards_initial_root_product_id={
                            props.tab_cards_initial_root_product_id
                        }
                    />
                </div>
            </div>
        </PageSkeleton>
    );
};
